import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { User, Lock, Loader2, Save, Trash2 } from "lucide-react";

export default function AccountSettings() {
  const { user, loading: authLoading, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ first_name: "", last_name: "", phone: "", sms_consent: false });
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || user === false)) navigate("/login");
    if (user && user !== false) {
      setProfile({ first_name: user.first_name || "", last_name: user.last_name || "", phone: user.phone || "", sms_consent: !!user.sms_consent });
    }
  }, [authLoading, user, navigate]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/account", profile);
      await checkAuth();
      toast.success("Profile updated successfully");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.new_password !== passwords.confirm_password) { toast.error("Passwords do not match"); return; }
    if (passwords.new_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSavingPassword(true);
    try {
      await api.post("/account/change-password", {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      setPasswords({ current_password: "", new_password: "", confirm_password: "" });
      toast.success("Password changed successfully");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm("Delete your account? Your information will be kept for 30 days, then permanently removed. You can cancel any time within those 30 days by logging back in.")) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/account/request-deletion");
      toast.success(data?.message || "Your account is scheduled for deletion.");
      try { await logout(); } catch { /* ignore */ }
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-paw-primary" /></div>;
  }

  return (
    <div data-testid="account-settings-page" className="py-12 md:py-20">
      <div className="max-w-2xl mx-auto px-6">
        <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-paw-text mb-2">Account Settings</h1>
        <p className="font-manrope text-sm text-paw-text-secondary mb-10">Manage your profile and security settings.</p>

        {/* Profile */}
        <form onSubmit={handleProfileSave} className="bg-white rounded-2xl border border-paw-border p-6 md:p-8 mb-6">
          <h2 className="font-outfit font-semibold text-lg text-paw-text flex items-center gap-2 mb-6">
            <User className="w-5 h-5 text-paw-primary" /> Profile Information
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-manrope text-sm font-medium text-paw-text">First Name</Label>
                <Input value={profile.first_name} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} className="rounded-xl border-paw-border" data-testid="settings-first-name" />
              </div>
              <div className="space-y-2">
                <Label className="font-manrope text-sm font-medium text-paw-text">Last Name</Label>
                <Input value={profile.last_name} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} className="rounded-xl border-paw-border" data-testid="settings-last-name" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-manrope text-sm font-medium text-paw-text">Email</Label>
              <Input value={user?.email || ""} disabled className="rounded-xl border-paw-border bg-paw-surface-alt cursor-not-allowed" data-testid="settings-email" />
              <p className="font-manrope text-xs text-paw-text-muted">Email cannot be changed.</p>
            </div>
            <div className="space-y-2">
              <Label className="font-manrope text-sm font-medium text-paw-text">Phone</Label>
              <Input type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+1 (555) 123-4567" className="rounded-xl border-paw-border" data-testid="settings-phone" />
            </div>
            {/* SMS consent. Never pre-checked: TCPA and toll-free verification both require
                express opt-in, and a pre-ticked box is not consent. */}
            <label className="flex items-start gap-3 rounded-xl border border-paw-border bg-paw-surface-alt/40 px-4 py-3 cursor-pointer" data-testid="settings-sms-consent-label">
              <input type="checkbox" checked={!!profile.sms_consent} onChange={(e) => setProfile({ ...profile, sms_consent: e.target.checked })} className="mt-0.5 w-4 h-4 rounded border-paw-border accent-paw-primary flex-shrink-0" data-testid="settings-sms-consent" />
              <span className="font-manrope text-xs text-paw-text-secondary leading-relaxed">
                <span className="block font-manrope font-semibold text-sm text-paw-text mb-1">Text me my verification codes and order updates.</span>
                By ticking this box you agree to receive one-time passcodes and order status texts from PawLinx at the mobile number above. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe, HELP for help. See our <Link to="/sms-terms" className="text-paw-accent font-medium hover:underline">SMS Terms</Link> and <Link to="/privacy" className="text-paw-accent font-medium hover:underline">Privacy Policy</Link>.
              </span>
            </label>
            <Button type="submit" disabled={savingProfile} className="rounded-xl bg-paw-primary hover:bg-paw-primary-hover text-white font-manrope" data-testid="save-profile-btn">
              {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </form>

        {/* Password */}
        <form onSubmit={handlePasswordChange} className="bg-white rounded-2xl border border-paw-border p-6 md:p-8">
          <h2 className="font-outfit font-semibold text-lg text-paw-text flex items-center gap-2 mb-6">
            <Lock className="w-5 h-5 text-paw-primary" /> Change Password
          </h2>
          <div className="space-y-4">
            {!user?.is_guest && (
              <div className="space-y-2">
                <Label className="font-manrope text-sm font-medium text-paw-text">Current Password</Label>
                <Input type="password" value={passwords.current_password} onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })} className="rounded-xl border-paw-border" data-testid="settings-current-password" />
              </div>
            )}
            <div className="space-y-2">
              <Label className="font-manrope text-sm font-medium text-paw-text">New Password</Label>
              <Input type="password" value={passwords.new_password} onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} placeholder="Min 6 characters" className="rounded-xl border-paw-border" data-testid="settings-new-password" />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope text-sm font-medium text-paw-text">Confirm New Password</Label>
              <Input type="password" value={passwords.confirm_password} onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })} className="rounded-xl border-paw-border" data-testid="settings-confirm-password" />
            </div>
            <Button type="submit" disabled={savingPassword} className="rounded-xl bg-paw-primary hover:bg-paw-primary-hover text-white font-manrope" data-testid="change-password-btn">
              {savingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              {user?.is_guest ? "Set Password" : "Change Password"}
            </Button>
          </div>
        </form>

        {/* Danger zone — account deletion with 30-day grace */}
        <div className="bg-white rounded-2xl border border-red-200 p-6 md:p-8 mt-6" data-testid="danger-zone">
          <h2 className="font-outfit font-semibold text-lg text-red-600 flex items-center gap-2 mb-3">
            <Trash2 className="w-5 h-5" /> Delete account
          </h2>
          <p className="font-manrope text-sm text-paw-text-secondary mb-5">
            This deactivates your account and schedules it for deletion. Your information is kept for <strong>30 days</strong>, then permanently removed. You can cancel any time within those 30 days by logging back in.
          </p>
          <Button type="button" onClick={handleDeleteAccount} disabled={deleting} className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-manrope" data-testid="delete-account-btn">
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Delete my account
          </Button>
        </div>
      </div>
    </div>
  );
}
